import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import {
  Briefcase, Code2, Layers, Server, Smartphone, Globe, Flame,
  ArrowUpRight, ChevronDown, BadgeCheck, ExternalLink, Plus,
  Phone, MessageCircle, Lock, FlaskConical, Rocket,
  ChevronLeft, ChevronRight, MoveRight,
} from 'lucide-react';

import AnimatedCard from '../components/AnimatedCard';
import SectionHeader from '../components/SectionHeader';
import Seo from '../seo/Seo';
import { PROJECTS_SEO } from '../seo/pageSeo';

import one from "../assets/one.png";
import two from "../assets/two.png";
import three from "../assets/three.png";
import four from "../assets/four.png";
import five from "../assets/five.png";

import done from "../assets/done.png";
import dtwo from "../assets/dtwo.png";
import dthree from "../assets/dthree.png";

/* ------------------------------------------------------------------
   DATA
   brand    = website ka naam (search me yahi naam aata hai)
   category = kis type ki website hai
------------------------------------------------------------------- */
const PROJECTS = [
  {
    brand: 'Strive Business Solution',
    category: 'Recruitment Company Website',
    tech: 'React.js · Framer Motion',
    url: 'https://www.strivebusinesssolution.com/',
    img: one,
    desc: 'Modern recruitment platform with smooth animations and professional design.',
  },
  {
    brand: 'BIS Bhiwani',
    category: 'Education & School Management Website',
    tech: 'React.js · Tailwind CSS',
    url: 'https://bisbhiwani.in/',
    img: three,
    desc: 'Complete school management & information system built for Bhiwani.',
  },
  {
    brand: 'InfoTech KS',
    category: 'Business Website',
    tech: 'React.js · Frontend',
    url: 'https://infotechks.com/',
    img: two,
    desc: 'Clean, conversion-focused business website for InfoTech KS.',
  },
  {
    brand: 'Trinity Global',
    category: 'IT Education Institute Website',
    tech: 'React.js · Frontend',
    url: 'https://www.trinityglobalofficial.com/',
    img: four,
    desc: 'Modern IT company website providing all courses.',
  },
  {
    brand: 'Big Bang Shoppers',
    category: 'Shopping Website',
    tech: 'React.js · Frontend',
    url: 'https://bigbangshoppers.com/',
    img: five,
    desc: 'Big Brands. One Address.',
  },
];

const CONCEPTS = [
  {
    brand: 'Hotel The Heera Divine',
    category: 'Hotel Website Concept',
    tech: 'Next.js · Tailwind',
    url: 'https://hoteltheheeradivine.vercel.app/',
    img: done,
    desc: 'Modern hotel experience with smooth UI.',
  },
  {
    brand: 'Ecommerce Store UI',
    category: 'Ecommerce Interface Concept',
    tech: 'React.js · Frontend',
    url: 'https://e-commerce-website-tau-ten.vercel.app/',
    img: dtwo,
    desc: 'Ecommerce platform interface design concept (Myntra-style layout; not affiliated with Myntra).',
  },
  {
    brand: 'GoStay Hotel',
    category: 'Hotel Booking Website Concept',
    tech: 'React.js · Charts',
    url: 'https://hotel-booking-website-blond.vercel.app/',
    img: dthree,
    desc: 'Interactive hotel booking UI with analytics dashboard.',
  },
];

const TECHS = [
  { label: 'React JS',      Icon: Code2 },
  { label: 'Next.js',       Icon: Globe },
  { label: 'Tailwind CSS',  Icon: Layers },
  { label: 'Node.js',       Icon: Server },
  { label: 'MongoDB',       Icon: Server },
  { label: 'Framer Motion', Icon: Smartphone },
  { label: 'Firebase',      Icon: Flame },
  { label: 'Vercel',        Icon: Globe },
];

const ACCENTS = ['#34d399', '#38bdf8', '#fbbf24', '#f472b6', '#a78bfa'];

// hero me tairte code-symbols
const GLYPHS = [
  { t: '</>',  left: '6%',  top: '18%', c: '#34d399', s: 34, d: 9,  dl: 0 },
  { t: '{ }',  left: '90%', top: '14%', c: '#38bdf8', s: 38, d: 11, dl: -3 },
  { t: '=>',   left: '12%', top: '62%', c: '#fbbf24', s: 30, d: 10, dl: -5 },
  { t: '#',    left: '84%', top: '58%', c: '#f472b6', s: 40, d: 8,  dl: -2 },
  { t: '[ ]',  left: '48%', top: '6%',  c: '#a78bfa', s: 28, d: 12, dl: -6 },
  { t: '( )',  left: '30%', top: '84%', c: '#34d399', s: 30, d: 9.5, dl: -4 },
  { t: '</',   left: '70%', top: '86%', c: '#38bdf8', s: 32, d: 10.5, dl: -7 },
];

const domainOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
};

const BY = 'VTCodex (vtcodex.com), a website & mobile app development company in India founded by Vishal Rana and Tanisha Garg';

/* ------------------------------------------------------------------
   SEO  –  AI / Google ko seedha batane ke liye: "X ko VTCodex ne banaya"
------------------------------------------------------------------- */
const FAQS = [
  {
    q: 'Which websites has VTCodex built?',
    a: `VTCodex has built ${PROJECTS.map(p => `${p.brand} (${domainOf(p.url)})`).join(', ')}. Each of these websites was designed and developed by ${BY}.`,
  },
  ...PROJECTS.map(p => ({
    q: `Who built ${p.brand} (${domainOf(p.url)})?`,
    a: `${p.brand} (${p.url}) – a ${p.category.toLowerCase()} – was designed and developed by ${BY}. Technology used: ${p.tech.replace(/ · /g, ', ')}.`,
  })),
  {
    q: 'Are the hotel and ecommerce projects client websites?',
    a: `No. ${CONCEPTS.map(c => c.brand).join(', ')} are concept and demo designs created by VTCodex to showcase UI and development skills. They are not live client businesses.`,
  },
  {
    q: 'Who founded VTCodex?',
    a: 'VTCodex was founded by Vishal Rana (Founder) and Tanisha Garg (Co-Founder).',
  },
  {
    q: 'Can VTCodex build a website like these for my business?',
    a: 'Yes. VTCodex builds business, school, college, institute, ecommerce and hotel websites with React and Next.js, plus Android apps. Contact VTCodex on WhatsApp or call +91 94991 30923 for a free quote.',
  },
];

const PROJECTS_PAGE_SCHEMA = [
  ...PROJECTS_SEO.schema.filter(s => s && s['@type'] !== 'ItemList'),
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': 'https://vtcodex.com/projects#list',
    name: 'Websites built by VTCodex',
    description: 'Client websites and concept designs designed and developed by VTCodex, founded by Vishal Rana and Tanisha Garg.',
    numberOfItems: PROJECTS.length + CONCEPTS.length,
    itemListElement: [...PROJECTS, ...CONCEPTS].map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'WebSite',
        name: p.brand,
        url: p.url,
        description: `${p.category} built by VTCodex. ${p.desc}`,
        creator: { '@id': 'https://vtcodex.com/#organization' },
        author: { '@id': 'https://vtcodex.com/#organization' },
        publisher: { '@id': 'https://vtcodex.com/#organization' },
        genre: p.category,
      },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
];

const PROJECTS_KEYWORDS = [
  PROJECTS_SEO.keywords,
  'websites built by VTCodex',
  'website developed by VTCodex',
  'VTCodex client projects',
  ...PROJECTS.flatMap(p => [p.brand, `${p.brand} developed by VTCodex`, `who built ${p.brand}`, domainOf(p.url)]),
  ...CONCEPTS.map(c => c.brand),
].join(', ');

const DOTS = Array.from({ length: 26 }, (_, i) => ({
  id: i,
  left: Math.random() * 100,
  top: Math.random() * 100,
  size: Math.random() * 4 + 2,
  color: ACCENTS[i % 3],
  dur: (Math.random() * 3 + 2).toFixed(1),
  delay: (Math.random() * 4).toFixed(1),
}));

/* ------------------------------------------------------------------
   Browser window frame (screenshot ko website jaisa dikhata hai)
------------------------------------------------------------------- */
function BrowserFrame({ url, children, accent, className = '' }) {
  return (
    <div className={`pj-frame ${className}`} style={{ '--ac': accent }}>
      <div className="pj-frame-bar">
        <span className="pj-dot" style={{ background: '#fb7185' }} />
        <span className="pj-dot" style={{ background: '#fbbf24' }} />
        <span className="pj-dot" style={{ background: '#34d399' }} />
        <span className="pj-url"><Lock size={11} /> {domainOf(url)}</span>
      </div>
      <div className="pj-frame-body">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Live client project card
------------------------------------------------------------------- */
function ProjectCard({ p, i }) {
  const accent = ACCENTS[i % ACCENTS.length];
  const featured = i === 0;
  const chips = p.tech.split(' · ');

  return (
    <AnimatedCard delay={(i % 2) * 0.1} className={featured ? 'lg:col-span-2' : ''}>
      <article
        className={`pj-card ${featured ? 'pj-card-featured' : ''}`}
        style={{ '--ac': accent }}
        itemScope
        itemType="https://schema.org/WebSite"
      >
        <meta itemProp="creator" content="VTCodex" />

        <a
          href={p.url}
          target="#"
          rel="noopener"
          className="pj-shot-link"
          aria-label={`Visit ${p.brand} – website built by VTCodex`}
          tabIndex={-1}
        >
          <BrowserFrame url={p.url} accent={accent}>
            <img
              src={p.img}
              alt={`${p.brand} – ${p.category} designed and developed by VTCodex`}
              loading={i === 0 ? 'eager' : 'lazy'}
              className="pj-shot"
            />
            <span className="pj-visit-overlay">
              <span className="pj-visit-pill">Visit Website <ArrowUpRight size={16} /></span>
            </span>
          </BrowserFrame>
        </a>

        <div className="pj-card-body">
          <span className="pj-cat">{p.category}</span>
          <h3 className="pj-card-title" itemProp="name">{p.brand}</h3>

          <ul className="pj-chips">
            {chips.map(c => <li key={c}>{c}</li>)}
          </ul>

          <p className="pj-card-desc" itemProp="description">{p.desc}</p>

          <p className="pj-built">
            <BadgeCheck size={18} color={accent} />
            <span>Designed &amp; developed by <b>VTCodex</b></span>
          </p>

          <a
            href={p.url}
            target="#"
            rel="noopener"
            itemProp="url"
            className="pj-link"
            aria-label={`Visit ${p.brand} website`}
          >
            <ExternalLink size={16} /> {domainOf(p.url)}
          </a>
        </div>
      </article>
    </AnimatedCard>
  );
}

/* ------------------------------------------------------------------
   Concept slider – swipe / drag / arrows / dots / auto-slide
------------------------------------------------------------------- */
const GAP = 24;
const stepOf = (el) => (el.firstElementChild ? el.firstElementChild.getBoundingClientRect().width : 300) + GAP;

function ConceptSlider({ items }) {
  const ref = useRef(null);
  const drag = useRef({ down: false, x: 0, left: 0, moved: false });
  const [idx, setIdx] = useState(0);
  const [edge, setEdge] = useState({ start: true, end: false });
  const [paused, setPaused] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIdx(Math.min(items.length - 1, Math.max(0, Math.round(el.scrollLeft / stepOf(el)))));
    setEdge({
      start: el.scrollLeft <= 2,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  }, [items.length]);

  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);

  const goTo = (i) => {
    const el = ref.current;
    if (el) el.scrollTo({ left: i * stepOf(el), behavior: 'smooth' });
  };
  const prev = () => { const el = ref.current; if (el) el.scrollBy({ left: -stepOf(el), behavior: 'smooth' }); };
  const next = () => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) el.scrollTo({ left: 0, behavior: 'smooth' });
    else el.scrollBy({ left: stepOf(el), behavior: 'smooth' });
  };

  // auto-slide (hover / touch / drag par ruk jata hai)
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      const el = ref.current;
      if (!el || el.scrollWidth <= el.clientWidth + 4) return;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) el.scrollTo({ left: 0, behavior: 'smooth' });
      else el.scrollBy({ left: stepOf(el), behavior: 'smooth' });
    }, 4500);
    return () => clearInterval(t);
  }, [paused]);

  // mouse drag (touch me native swipe chalta hai)
  const onMouseDown = (e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, x: e.clientX, left: el.scrollLeft, moved: false };
    el.style.scrollSnapType = 'none';
    el.style.scrollBehavior = 'auto';
  };
  const onMouseMove = (e) => {
    const d = drag.current;
    if (!d.down) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 5) d.moved = true;
    ref.current.scrollLeft = d.left - dx;
  };
  const endDrag = () => {
    const el = ref.current;
    if (!drag.current.down || !el) return;
    drag.current.down = false;
    el.style.scrollSnapType = '';
    el.style.scrollBehavior = '';
    update();
  };
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return (
    <div
      className="pj-slider-wrap"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); endDrag(); }}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), 6000)}
    >
      <div
        ref={ref}
        className="pj-slider"
        onScroll={update}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onClickCapture={onClickCapture}
        onDragStart={(e) => e.preventDefault()}
        role="region"
        aria-label="Concept projects slider"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') next();
          if (e.key === 'ArrowLeft') prev();
        }}
      >
        {items.map((p, i) => {
          const accent = ACCENTS[(i + 2) % ACCENTS.length];
          return (
            <article key={p.brand} className="pj-concept" style={{ '--ac': accent }}>
              <a
                href={p.url}
                target="#"
                rel="noopener"
                draggable={false}
                className="no-underline"
                aria-label={`Open ${p.brand} concept project by VTCodex`}
              >
                <BrowserFrame url={p.url} accent={accent}>
                  <img
                    src={p.img}
                    alt={`${p.brand} – ${p.category} designed by VTCodex`}
                    loading="lazy"
                    draggable={false}
                    className="pj-shot"
                    style={{ animationDelay: `${-i * 3}s` }}
                  />
                  <span className="pj-visit-overlay">
                    <span className="pj-visit-pill">Open Concept <ArrowUpRight size={16} /></span>
                  </span>
                </BrowserFrame>
                <div className="pj-concept-body">
                  <span className="pj-cat"><FlaskConical size={12} /> {p.category}</span>
                  <h3 className="pj-card-title" style={{ fontSize: '1.45rem' }}>{p.brand}</h3>
                  <p className="pj-card-desc" style={{ fontSize: '.95rem', marginTop: '.5rem' }}>{p.desc}</p>
                  <p className="pj-tech-line">{p.tech}</p>
                  <p className="pj-built" style={{ margin: '.9rem 0 0' }}>
                    <BadgeCheck size={17} color={accent} />
                    <span>Designed by <b>VTCodex</b></span>
                  </p>
                </div>
              </a>
            </article>
          );
        })}
      </div>

      {/* controls */}
      <div className="pj-controls">
        <button type="button" onClick={prev} disabled={edge.start} aria-label="Previous project" className="pj-arrow">
          <ChevronLeft size={24} />
        </button>

        <div className="flex flex-col items-center gap-3">
          <div className="pj-dots" role="tablist" aria-label="Choose concept project">
            {items.map((p, i) => (
              <button
                key={p.brand}
                type="button"
                role="tab"
                aria-selected={i === idx}
                aria-label={`Go to ${p.brand}`}
                onClick={() => goTo(i)}
                className={i === idx ? 'pj-dotb on' : 'pj-dotb'}
              />
            ))}
          </div>
          <p className="pj-hint">
            <span>Swipe or drag</span> <MoveRight size={15} className="pj-hint-arrow" />
          </p>
        </div>

        <button type="button" onClick={next} aria-label="Next project" className="pj-arrow">
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   PAGE
------------------------------------------------------------------- */
export default function ProjectsPage() {
  const concepts = useMemo(() => CONCEPTS, []);

  return (
    <div className="pj-page">
      <Seo
        {...PROJECTS_SEO}
        title="Websites Built by VTCodex – Client Projects & Portfolio | Strive Business Solution, BIS Bhiwani, InfoTech KS"
        description="Websites designed & developed by VTCodex (founded by Vishal Rana & Tanisha Garg): Strive Business Solution, BIS Bhiwani, InfoTech KS, Trinity Global, Big Bang Shoppers and more. React & Next.js website development company in India."
        keywords={PROJECTS_KEYWORDS}
        schema={PROJECTS_PAGE_SCHEMA}
      />

      {/* ============================ HERO ============================ */}
      <section className="pj-hero relative overflow-hidden" style={{ minHeight: '94vh' }}>

        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 pj-hero-bg" />
          <span className="pj-orb" style={{ left: '-8%', top: '15%', width: 460, background: 'radial-gradient(circle, rgba(52,211,153,.26), transparent 70%)' }} />
          <span className="pj-orb" style={{ right: '-8%', top: '5%', width: 520, background: 'radial-gradient(circle, rgba(56,189,248,.22), transparent 70%)', animationDelay: '-6s' }} />
          <span className="pj-orb" style={{ left: '35%', bottom: '-10%', width: 480, background: 'radial-gradient(circle, rgba(251,191,36,.14), transparent 70%)', animationDelay: '-11s' }} />
          <div className="absolute inset-0 pj-grid" />
          <span className="pj-beam pj-beam-1" />
          <span className="pj-beam pj-beam-2" />
          {GLYPHS.map(g => (
            <span
              key={g.t + g.left}
              className="pj-glyph"
              style={{
                left: g.left, top: g.top, color: g.c, fontSize: g.s,
                textShadow: `0 0 18px ${g.c}`,
                animationDuration: `${g.d}s`, animationDelay: `${g.dl}s`,
              }}
            >
              {g.t}
            </span>
          ))}
          {DOTS.map(d => (
            <span
              key={d.id}
              className="absolute rounded-full"
              style={{
                left: `${d.left}%`, top: `${d.top}%`, width: d.size, height: d.size,
                background: d.color, boxShadow: `0 0 ${d.size * 4}px ${d.color}`,
                animation: `pjDot ${d.dur}s ease-in-out ${d.delay}s infinite alternate`, opacity: .6,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 pt-24 pb-16 flex flex-col items-center text-center">

          <div className="pj-pill mb-8">
            <Briefcase size={14} /> REAL PROJECTS · REAL CLIENTS
          </div>

          <h1 className="pj-h1">
            Websites Built <br />
            <span className="pj-grad">By VTCodex</span>
          </h1>

          <p className="pj-lead">
            Every website on this page was <b>designed and developed by VTCodex</b> —
            founded by <b>Vishal Rana</b> and <b>Tanisha Garg</b>. Fast, modern, SEO-ready
            websites for businesses, schools, institutes and shops across India.
          </p>

          {/* stats */}
          <div className="pj-stats">
            <div><b>{PROJECTS.length}</b><span>Live Client Websites</span></div>
            <div><b>{CONCEPTS.length}</b><span>Concept Designs</span></div>
            <div><b>React</b><span>&amp; Next.js</span></div>
          </div>

          {/* floating browser shots */}
          <div className="pj-hero-shots" aria-hidden="true">
            {[three, one, two].map((img, i) => (
              <div key={i} className={`pj-hs pj-hs-${i}`}>
                <BrowserFrame url={[PROJECTS[1], PROJECTS[0], PROJECTS[2]][i].url} accent={ACCENTS[i]}>
                  <img src={img} alt="" className="pj-shot" />
                </BrowserFrame>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-3 mt-14">
            {TECHS.map(({ label, Icon }, i) => (
              <span
                key={label}
                className="pj-tech-pill"
                style={{ '--ac': ACCENTS[i % 3], animationDelay: `${-i * 0.45}s` }}
              >
                <Icon size={13} /> {label}
              </span>
            ))}
          </div>

          <a href="#live-projects" className="mt-14" aria-label="Scroll to projects">
            <ChevronDown size={24} color="#34d399" style={{ animation: 'pjBounce 1.5s infinite' }} />
          </a>
        </div>
      </section>

      {/* ========================= TECH TICKER ======================== */}
      <div className="pj-ticker" aria-label="Technologies used by VTCodex">
        <div className="pj-ticker-track">
          {[0, 1].map(copy => (
            <div key={copy} className="pj-ticker-set" aria-hidden={copy === 1 || undefined}>
              {TECHS.concat(TECHS).map(({ label, Icon }, i) => (
                <span key={`${copy}-${i}`} className="pj-ticker-item">
                  <Icon size={16} color={ACCENTS[i % 3]} /> {label}
                  <i style={{ background: ACCENTS[(i + 1) % 3] }} />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ======================= LIVE PROJECTS ======================== */}
      <section id="live-projects" className="pj-sec-a relative px-6 sm:px-8 py-24 overflow-hidden">
        <span className="pj-orb" style={{ left: '-10%', top: '20%', width: 420, background: 'radial-gradient(circle, rgba(52,211,153,.14), transparent 70%)' }} />
        <span className="pj-shape pj-ring" style={{ right: '4%', top: '6%', borderColor: '#38bdf8' }} />
        <span className="pj-shape pj-sq" style={{ left: '3%', top: '40%', borderColor: '#fbbf24', animationDelay: '-3s' }} />
        <span className="pj-shape pj-ring" style={{ left: '8%', bottom: '6%', width: 34, height: 34, borderColor: '#34d399', animationDelay: '-5s' }} />
        <span className="pj-shape pj-sq" style={{ right: '6%', bottom: '14%', width: 30, height: 30, borderColor: '#f472b6', animationDelay: '-7s' }} />
        <div className="relative max-w-6xl mx-auto">
          <SectionHeader
            label="OUR WORK"
            title="Live Client Projects"
            sub="Real websites, built by VTCodex, running for real businesses"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
            {PROJECTS.map((p, i) => (
              <ProjectCard key={p.url} p={p} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ===================== PROJECT DIRECTORY ====================== */}
      <section className="pj-sec-b px-6 sm:px-8 py-24" aria-labelledby="pj-dir-h">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            label="QUICK INDEX"
            title="Websites Developed by VTCodex"
            sub="Project name, website, type and technology — all in one place"
          />

          <AnimatedCard delay={0.05}>
            <div className="pj-table-wrap">
              <table className="pj-table">
                <caption className="sr-only" id="pj-dir-h">List of websites developed by VTCodex</caption>
                <thead>
                  <tr>
                    <th scope="col">Project</th>
                    <th scope="col">Website</th>
                    <th scope="col">Type</th>
                    <th scope="col">Technology</th>
                    <th scope="col">Developed by</th>
                  </tr>
                </thead>
                <tbody>
                  {PROJECTS.map((p, i) => (
                    <tr key={p.url}>
                      <th scope="row">
                        <span className="pj-dot-ac" style={{ background: ACCENTS[i % ACCENTS.length] }} /> {p.brand}
                      </th>
                      <td><a href={p.url} target="#" rel="noopener">{domainOf(p.url)}</a></td>
                      <td>{p.category}</td>
                      <td>{p.tech}</td>
                      <td><b>VTCodex</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ======================== CONCEPTS ============================ */}
      <section className="pj-sec-c relative py-24 overflow-hidden">
        <span className="pj-orb" style={{ right: '-8%', top: '10%', width: 460, background: 'radial-gradient(circle, rgba(167,139,250,.16), transparent 70%)' }} />
        <span className="pj-orb" style={{ left: '-8%', bottom: '0%', width: 420, background: 'radial-gradient(circle, rgba(56,189,248,.14), transparent 70%)', animationDelay: '-9s' }} />
        <span className="pj-shape pj-ring" style={{ left: '5%', top: '12%', borderColor: '#a78bfa' }} />
        <span className="pj-shape pj-sq" style={{ right: '5%', bottom: '22%', borderColor: '#34d399', animationDelay: '-4s' }} />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 mb-12">
          <SectionHeader
            label="SHOWCASE"
            title="Concept & Demo Projects"
            sub="Slide through creative UI concepts and premium designs by VTCodex"
          />
        </div>
        <ConceptSlider items={concepts} />
      </section>

      {/* ============================ FAQ ============================= */}
      <section className="pj-sec-b px-6 sm:px-8 py-24" aria-labelledby="pj-faq-h">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            label="QUESTIONS"
            title="Who Built These Websites?"
            sub="Quick answers about VTCodex projects"
          />
          <h2 id="pj-faq-h" className="sr-only">Frequently asked questions about VTCodex projects</h2>

          <div className="mt-12 flex flex-col gap-4">
            {FAQS.map((f, i) => (
              <AnimatedCard key={f.q} delay={(i % 4) * 0.05}>
                <details className="pj-faq" open={i === 0}>
                  <summary>
                    <span>{f.q}</span>
                    <Plus size={20} className="pj-faq-plus" />
                  </summary>
                  <p>{f.a}</p>
                </details>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ CTA ============================= */}
      <section className="pj-sec-c px-6 sm:px-8 pb-28 pt-8">
        <div className="max-w-5xl mx-auto">
          <AnimatedCard delay={0.2}>
            <div className="pj-cta">
              <Rocket size={38} color="#34d399" className="mx-auto mb-4 pj-rocket" />
              <h2 className="pj-cta-title">Want A Website Like These?</h2>
              <p className="pj-cta-sub">
                Tell VTCodex about your business — we'll design, build and launch a modern,
                SEO-ready website for you.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://wa.me/919499130923"
                  target="#"
                  rel="noopener noreferrer"
                  className="pj-btn inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-bold no-underline"
                >
                  <MessageCircle size={19} /> Let's Talk
                </a>
                <a href="tel:+919499130923" className="pj-btn-ghost inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-bold no-underline">
                  <Phone size={17} /> +91 9499130923
                </a>
              </div>
            </div>
          </AnimatedCard>
        </div>
      </section>

      {/* ============================ STYLES ========================== */}
      <style>{`
        .pj-page {
          background:#060a0f; color:#e2e8f0;
          -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; text-rendering:optimizeLegibility;
        }
        /* reveal ke baad transform hata do – text blurry na ho */
        .pj-page .reveal-card.visible { transform:none !important; }
        .pj-page .section-title { color:#fff; }
        .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }

        /* ---------- hero ---------- */
        .pj-hero-bg {
          background:
            radial-gradient(ellipse at 50% 0%, rgba(52,211,153,.14), transparent 60%),
            linear-gradient(180deg, #060a0f 0%, #08101a 100%);
        }
        .pj-grid {
          opacity:.5;
          background-image: linear-gradient(rgba(148,163,184,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.07) 1px, transparent 1px);
          background-size: 48px 48px;
          -webkit-mask-image: radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%);
                  mask-image: radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%);
        }
        .pj-orb { position:absolute; aspect-ratio:1/1; border-radius:9999px; filter: blur(60px); pointer-events:none; animation: pjDrift 18s ease-in-out infinite alternate; }
        .pj-pill {
          display:inline-flex; align-items:center; gap:.5rem; padding:.5rem 1.25rem; border-radius:9999px;
          font-size:.75rem; font-weight:800; letter-spacing:.2em; color:#a7f3d0;
          background: rgba(52,211,153,.1); border:1px solid rgba(52,211,153,.45);
        }
        .pj-h1 { font-family:'Space Grotesk',sans-serif; font-weight:700; color:#fff; font-size: clamp(2.8rem, 8vw, 6rem); line-height:1.02; letter-spacing:-.02em; margin-bottom:1.5rem; }
        .pj-grad {
          background: linear-gradient(90deg,#34d399,#38bdf8,#fbbf24,#34d399); background-size:300% 100%;
          -webkit-background-clip:text; background-clip:text; color:transparent; animation: pjHue 7s linear infinite;
        }
        .pj-lead { max-width: 46rem; color:#e2e8f0; font-size:1.15rem; line-height:1.8; font-weight:500; margin-bottom:2.5rem; }
        .pj-lead b { color:#fff; }
        .pj-stats { display:flex; flex-wrap:wrap; justify-content:center; gap:14px; margin-bottom:3.5rem; }
        .pj-stats > div {
          min-width:150px; padding:.9rem 1.4rem; border-radius:20px; background:#0e1520; border:1px solid rgba(148,163,184,.25);
          display:flex; flex-direction:column; align-items:center;
        }
        .pj-stats b { font-family:'Space Grotesk',sans-serif; font-size:2rem; color:#34d399; line-height:1.1; }
        .pj-stats span { font-size:.8rem; font-weight:700; color:#cbd5e1; letter-spacing:.04em; }

        .pj-hero-shots { position:relative; display:flex; justify-content:center; align-items:flex-end; width:100%; max-width:900px; height: 250px; }
        .pj-hs { position:absolute; width: min(46vw, 330px); animation: pjFloat 6s ease-in-out infinite; }
        .pj-hs-0 { left:2%;  bottom:0; transform: rotate(-6deg); animation-delay:-2s; }
        .pj-hs-1 { left:50%; bottom:20px; margin-left:calc(min(46vw,330px) / -2); z-index:2; animation-delay:0s; }
        .pj-hs-2 { right:2%; bottom:0; transform: rotate(6deg); animation-delay:-4s; }
        .pj-hs-0, .pj-hs-2 { display:none; }
        @media (min-width: 700px) { .pj-hs-0, .pj-hs-2 { display:block; } .pj-hero-shots { height: 300px; } }
        .pj-tech-pill {
          display:inline-flex; align-items:center; gap:.5rem; padding:.45rem 1rem; border-radius:9999px; font-size:.85rem; font-weight:700;
          color:#fff; background:#0e1520; border:1px solid color-mix(in srgb, var(--ac) 55%, transparent);
        }
        .pj-tech-pill svg { color: var(--ac); }

        /* ---------- sections ---------- */
        .pj-sec-a { background:#08101a; }
        .pj-sec-b { background:#060a0f; }
        .pj-sec-c { background:linear-gradient(180deg,#08101a,#060a0f); }

        /* ---------- browser frame ---------- */
        .pj-frame {
          border-radius:16px; overflow:hidden; background:#0b1018; border:1px solid rgba(148,163,184,.3);
          box-shadow: 0 20px 44px rgba(0,0,0,.55);
        }
        .pj-frame-bar { display:flex; align-items:center; gap:6px; padding:9px 12px; background:#131c2b; border-bottom:1px solid rgba(148,163,184,.2); }
        .pj-dot { width:10px; height:10px; border-radius:9999px; }
        .pj-url {
          margin-left:10px; flex:1; min-width:0; display:inline-flex; align-items:center; gap:6px; padding:3px 12px; border-radius:9999px;
          background:#0b1018; color:#cbd5e1; font-family:'JetBrains Mono',monospace; font-size:.72rem;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .pj-frame-body { position:relative; aspect-ratio: 16 / 10; overflow:hidden; background:#0b1018; }
        .pj-shot { width:100%; height:100%; object-fit:cover; object-position: 50% 0%; display:block; animation: pjPan 14s ease-in-out infinite alternate; }
        .pj-visit-overlay {
          position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
          background: rgba(6,10,15,.55); opacity:0; transition: opacity .3s;
        }
        .pj-visit-pill {
          display:inline-flex; align-items:center; gap:.5rem; padding:.7rem 1.4rem; border-radius:9999px; font-weight:800; color:#052e2b;
          background: linear-gradient(135deg,#34d399,#38bdf8);
        }

        /* ---------- project card ---------- */
        .pj-card {
          position:relative; height:100%; border-radius:30px; overflow:hidden; background:#0e1520;
          border:1px solid rgba(148,163,184,.22); padding:18px;
          transition: transform .35s, border-color .35s, box-shadow .35s;
        }
        .pj-card::before {
          content:''; position:absolute; left:0; top:0; right:0; height:4px;
          background: linear-gradient(90deg, var(--ac), transparent);
        }
        .pj-card:hover { transform: translateY(-8px); border-color: var(--ac); box-shadow: 0 26px 60px rgba(0,0,0,.6), 0 0 40px color-mix(in srgb, var(--ac) 25%, transparent); }
                .pj-card:hover .pj-visit-overlay, .pj-concept:hover .pj-visit-overlay { opacity:1; }
        .pj-shot-link { display:block; }
        .pj-card-body { padding: 22px 8px 8px; }
        .pj-card-featured { display:grid; grid-template-columns:1fr; gap:10px; }
        @media (min-width: 1024px) {
          .pj-card-featured { grid-template-columns: 1.25fr 1fr; align-items:center; gap:30px; padding:26px; }
          .pj-card-featured .pj-card-title { font-size: 2.6rem; }
        }
        .pj-cat {
          display:inline-flex; align-items:center; gap:.4rem; font-size:.74rem; font-weight:800; letter-spacing:.12em; text-transform:uppercase;
          color: var(--ac); margin-bottom:.5rem;
        }
        .pj-card-title { font-family:'Space Grotesk',sans-serif; font-weight:700; color:#fff; font-size:1.9rem; line-height:1.1; letter-spacing:-.01em; }
        .pj-chips { display:flex; flex-wrap:wrap; gap:8px; list-style:none; padding:0; margin:1rem 0; }
        .pj-chips li { padding:.3rem .8rem; border-radius:9999px; font-size:.8rem; font-weight:700; color:#fff; background:#182235; border:1px solid color-mix(in srgb, var(--ac) 50%, transparent); }
        .pj-card-desc { color:#e2e8f0; line-height:1.7; font-weight:500; }
        .pj-built { display:flex; align-items:center; gap:.55rem; margin:1.1rem 0; color:#f1f5f9; font-size:.95rem; font-weight:600; }
        .pj-built b { color:var(--ac); }
        .pj-link {
          display:inline-flex; align-items:center; gap:.5rem; padding:.7rem 1.2rem; border-radius:14px; font-weight:800; font-size:.92rem; text-decoration:none;
          color:#06121a; background: var(--ac); box-shadow: 0 5px 0 color-mix(in srgb, var(--ac) 45%, #000);
          transition: transform .15s, box-shadow .15s; font-family:'JetBrains Mono',monospace;
        }
        .pj-link:hover { transform: translateY(-2px); }
        .pj-link:active { transform: translateY(4px); box-shadow:0 1px 0 #000; }

        /* ---------- directory table ---------- */
        .pj-table-wrap { overflow-x:auto; border-radius:24px; border:1px solid rgba(148,163,184,.25); background:#0e1520; }
        .pj-table { width:100%; min-width:760px; border-collapse:collapse; text-align:left; }
        .pj-table th, .pj-table td { padding:1rem 1.2rem; border-bottom:1px solid rgba(148,163,184,.15); color:#e2e8f0; font-weight:500; }
        .pj-table thead th { background:#131c2b; color:#a7f3d0; font-size:.78rem; letter-spacing:.14em; text-transform:uppercase; font-weight:800; }
        .pj-table tbody th { color:#fff; font-weight:700; white-space:nowrap; }
        .pj-table tbody tr:last-child th, .pj-table tbody tr:last-child td { border-bottom:none; }
        .pj-table tbody tr:hover { background: rgba(52,211,153,.06); }
        .pj-table a { color:#38bdf8; font-weight:700; font-family:'JetBrains Mono',monospace; font-size:.9rem; }
        .pj-table b { color:#34d399; }
        .pj-dot-ac { display:inline-block; width:10px; height:10px; border-radius:9999px; margin-right:.5rem; }

        /* ---------- concept slider ---------- */
        .pj-slider-wrap { position:relative; }
        .pj-slider {
          display:flex; gap:24px; overflow-x:auto; overflow-y:hidden;
          scroll-snap-type:x mandatory; scroll-behavior:smooth; -webkit-overflow-scrolling:touch;
          padding: 24px max(24px, calc((100vw - 1152px) / 2)) 30px;
          scroll-padding: 0 max(24px, calc((100vw - 1152px) / 2));
          scrollbar-width:none; cursor:grab; outline:none; user-select:none; -webkit-user-select:none;
        }
        .pj-slider::-webkit-scrollbar { display:none; }
        .pj-slider:active { cursor:grabbing; }
        .pj-concept {
          flex:0 0 auto; width: min(82vw, 410px); scroll-snap-align:start; border-radius:26px; overflow:hidden; padding:14px;
          background:#0e1520; border:1px solid rgba(148,163,184,.22); transition: border-color .3s, box-shadow .3s, transform .3s;
        }
        .pj-concept:hover { border-color: var(--ac); transform: translateY(-6px); box-shadow: 0 22px 50px rgba(0,0,0,.6), 0 0 34px color-mix(in srgb, var(--ac) 22%, transparent); }
        .pj-concept a { display:block; color:inherit; }
        .pj-concept-body { padding: 18px 6px 6px; }
        .pj-tech-line { margin-top:.7rem; font-family:'JetBrains Mono',monospace; font-size:.78rem; color:#cbd5e1; }

        .pj-controls { display:flex; align-items:center; justify-content:center; gap:22px; margin-top:6px; }
        .pj-arrow {
          width:54px; height:54px; border-radius:9999px; display:flex; align-items:center; justify-content:center; cursor:pointer;
          color:#34d399; background:#0e1520; border:2px solid rgba(52,211,153,.55); box-shadow: 0 5px 0 rgba(52,211,153,.25);
          transition: transform .15s, background .2s, box-shadow .15s, opacity .2s;
        }
        .pj-arrow:hover:not(:disabled) { transform: translateY(-3px); background: rgba(52,211,153,.14); }
        .pj-arrow:active:not(:disabled) { transform: translateY(3px); box-shadow:0 1px 0 rgba(52,211,153,.25); }
        .pj-arrow:disabled { opacity:.35; cursor:not-allowed; }
        .pj-dots { display:flex; gap:8px; align-items:center; }
        .pj-dotb { width:10px; height:10px; padding:0; border:none; border-radius:9999px; background:rgba(148,163,184,.45); cursor:pointer; transition: width .3s, background .3s; }
        .pj-dotb.on { width:32px; background: linear-gradient(90deg,#34d399,#38bdf8); }
        .pj-hint { display:flex; align-items:center; gap:.5rem; font-size:.8rem; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:#cbd5e1; }
        .pj-hint-arrow { color:#34d399; animation: pjNudge 1.4s ease-in-out infinite; }

        /* ---------- FAQ ---------- */
        .pj-faq { border-radius:22px; background:#0e1520; border:1px solid rgba(148,163,184,.22); transition: border-color .3s; }
        .pj-faq[open] { border-color: rgba(52,211,153,.6); }
        .pj-faq summary {
          display:flex; align-items:center; justify-content:space-between; gap:1rem; padding:1.2rem 1.4rem;
          cursor:pointer; list-style:none; color:#fff; font-weight:700; font-size:1.05rem; line-height:1.4;
        }
        .pj-faq summary::-webkit-details-marker { display:none; }
        .pj-faq-plus { flex-shrink:0; color:#34d399; transition: transform .3s; }
        .pj-faq[open] .pj-faq-plus { transform: rotate(45deg); }
        .pj-faq p { padding: 0 1.4rem 1.3rem; color:#e2e8f0; line-height:1.75; font-weight:500; }

        /* ---------- CTA ---------- */
        .pj-cta {
          text-align:center; padding: 3.2rem 1.6rem; border-radius:34px; position:relative; overflow:hidden;
          background: linear-gradient(160deg,#0f2a2a 0%, #0e1520 60%); border:2px solid rgba(52,211,153,.5);
          box-shadow: 0 0 60px rgba(52,211,153,.12);
        }
        .pj-cta-title { font-family:'Space Grotesk',sans-serif; font-weight:700; color:#fff; font-size: clamp(2rem, 5vw, 3.2rem); margin-bottom:.8rem; }
        .pj-cta-sub { color:#e2e8f0; max-width:36rem; margin:0 auto 2rem; line-height:1.7; font-weight:500; }
        .pj-btn {
          background: linear-gradient(135deg,#34d399,#38bdf8); color:#052e2b;
          box-shadow: 0 6px 0 #0f766e, 0 14px 30px rgba(52,211,153,.35); transition: transform .15s, box-shadow .15s;
        }
        .pj-btn:hover { transform: translateY(-3px); }
        .pj-btn:active { transform: translateY(5px); box-shadow:0 1px 0 #0f766e; }
        .pj-btn-ghost { color:#a7f3d0; border:2px solid rgba(52,211,153,.6); background:rgba(52,211,153,.08); box-shadow:0 6px 0 rgba(52,211,153,.25); transition: transform .15s; }
        .pj-btn-ghost:hover { transform: translateY(-3px); }


        /* ---------- extra motion ---------- */
        .pj-glyph { position:absolute; font-family:'JetBrains Mono',monospace; font-weight:800; opacity:.55; animation: pjGlyph 10s ease-in-out infinite; }
        .pj-beam { position:absolute; height:2px; width:260px; border-radius:9999px; opacity:.7; }
        .pj-beam-1 { top:28%; left:-300px; background: linear-gradient(90deg, transparent, #34d399, transparent); animation: pjBeam 7s linear infinite; }
        .pj-beam-2 { top:68%; left:-300px; background: linear-gradient(90deg, transparent, #38bdf8, transparent); animation: pjBeam 9s linear -4s infinite; }
        .pj-tech-pill { animation: pjBob 3.6s ease-in-out infinite; }
        .pj-stats > div { animation: pjBob 4.4s ease-in-out infinite; }
        .pj-stats > div:nth-child(2) { animation-delay:-1.5s; }
        .pj-stats > div:nth-child(3) { animation-delay:-3s; }
        .pj-rocket { animation: pjRocket 3s ease-in-out infinite; }

        .pj-shape { position:absolute; width:46px; height:46px; border:3px solid; opacity:.6; pointer-events:none; animation: pjSpinFloat 12s ease-in-out infinite; }
        .pj-ring { border-radius:9999px; }
        .pj-sq   { border-radius:12px; }

        .pj-frame-body::after {
          content:''; position:absolute; top:0; bottom:0; left:-60%; width:35%;
          background: linear-gradient(100deg, transparent, rgba(255,255,255,.13), transparent);
          transform: skewX(-18deg); animation: pjShine 7s ease-in-out infinite; pointer-events:none;
        }

        .pj-ticker { overflow:hidden; background:#0b1420; border-top:1px solid rgba(148,163,184,.2); border-bottom:1px solid rgba(148,163,184,.2); padding:16px 0; }
        .pj-ticker-track { display:flex; width:max-content; animation: pjTicker 40s linear infinite; }
        .pj-ticker-set { display:flex; flex-shrink:0; }
        .pj-ticker-item { display:inline-flex; align-items:center; gap:.6rem; padding:0 1.4rem; color:#fff; font-weight:700; font-size:.95rem; white-space:nowrap; }
        .pj-ticker-item i { width:6px; height:6px; border-radius:9999px; margin-left:1.4rem; }

        /* ---------- keyframes ---------- */
        @keyframes pjDot    { from { opacity:.25; transform:scale(1); } to { opacity:.8; transform:scale(1.7); } }
        @keyframes pjDrift  { from { transform: translate(0,0) scale(1); } to { transform: translate(50px,-40px) scale(1.15); } }
        @keyframes pjFloat  { 0%,100% { translate: 0 0; } 50% { translate: 0 -14px; } }
        @keyframes pjHue    { to { background-position: 300% 0; } }
        @keyframes pjBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(10px); } }
        @keyframes pjPan    { from { object-position: 50% 0%; } to { object-position: 50% 100%; } }
        @keyframes pjGlyph  { 0%,100% { transform: translate(0,0) rotate(0deg); } 33% { transform: translate(18px,-26px) rotate(8deg); } 66% { transform: translate(-14px,-12px) rotate(-6deg); } }
        @keyframes pjBeam   { from { transform: translateX(0); } to { transform: translateX(calc(100vw + 600px)); } }
        @keyframes pjBob    { 0%,100% { translate: 0 0; } 50% { translate: 0 -7px; } }
        @keyframes pjRocket { 0%,100% { transform: translate(0,0) rotate(-8deg); } 50% { transform: translate(8px,-12px) rotate(8deg); } }
        @keyframes pjSpinFloat { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-30px) rotate(180deg); } }
        @keyframes pjShine  { 0%,55% { left:-60%; } 100% { left:140%; } }
        @keyframes pjTicker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes pjNudge  { 0%,100% { transform: translateX(0); } 50% { transform: translateX(8px); } }

        @media (prefers-reduced-motion: reduce) {
          .pj-page *, .pj-page *::before, .pj-page *::after { animation:none !important; transition:none !important; }
        }
      `}</style>
    </div>
  );
}